import { getApiUrl } from "@/utils/api";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = getApiUrl();
const LOGO_URL = "https://customer-assets.emergentagent.com/job_realty-gallery/artifacts/zzc7uqc2_Screenshot_20260119_035617_Chrome.ico";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ username: "", password: "" });

  // Forgot password modal
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // If already authenticated, redirect to /admin
  useEffect(() => {
    const check = async () => {
      try {
        await axios.get(`${API}/auth/me`, { withCredentials: true });
        navigate("/admin", { replace: true });
      } catch {
        // Not authenticated — show form
      } finally {
        setChecking(false);
      }
    };
    check();
  }, [navigate]);

  const handleChange = (e) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const username = form.username.trim();
    const password = form.password;
    if (!username || !password) {
      toast.error("Please enter both username and password");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/admin/auth/login`,
        { username, password },
        { withCredentials: true }
      );
      toast.success("Welcome back");
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Invalid credentials";
      toast.error(msg);
      setForm((p) => ({ ...p, password: "" }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    const email = forgotEmail.trim();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setForgotSending(true);
    try {
      await axios.post(`${API}/admin/auth/forgot-password`, { email });
      setForgotSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send reset email");
    } finally {
      setForgotSending(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30" data-testid="admin-login-page">
      <div className="w-full max-w-md p-8">
        <div className="glass-card p-10 space-y-8">
          {/* Logo & Title */}
          <div className="text-center space-y-4">
            <img src={LOGO_URL} alt="SellLocalHomes" className="h-16 w-16 mx-auto" />
            <h1 className="font-serif text-3xl font-medium">Admin Portal</h1>
            <p className="text-muted-foreground">Sign in to manage your site</p>
          </div>

          <div className="section-divider mx-auto" />

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="admin-username">Username</Label>
              <Input
                id="admin-username"
                name="username"
                type="text"
                value={form.username}
                onChange={handleChange}
                placeholder="admin"
                className="h-12 rounded-none"
                autoComplete="username"
                data-testid="admin-username-input"
                disabled={submitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin-password">Password</Label>
              <div className="relative">
                <Input
                  id="admin-password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  className="h-12 rounded-none pr-12"
                  autoComplete="current-password"
                  data-testid="admin-password-input"
                  disabled={submitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting || !form.username || !form.password}
              className="w-full h-12 rounded-none text-base font-medium"
              data-testid="admin-signin-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Signing in…
                </>
              ) : (
                "Sign In"
              )}
            </Button>

            <button
              type="button"
              onClick={() => { setForgotOpen(true); setForgotSent(false); setForgotEmail(""); }}
              className="block w-full text-center text-sm text-muted-foreground hover:text-foreground link-underline"
              data-testid="admin-forgot-password-link"
            >
              Forgot password?
            </button>
          </form>

          <p className="text-xs text-center text-muted-foreground">
            Master administrator access only
          </p>
        </div>

        <div className="mt-6 text-center">
          <a
            href="/"
            className="text-sm text-muted-foreground hover:text-foreground link-underline"
            data-testid="back-to-site-link"
          >
            ← Back to SellLocalHomes
          </a>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="sm:max-w-md rounded-none" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Reset your password</DialogTitle>
            <DialogDescription>
              Enter the recovery email registered to your master admin account. We'll send you a one-time link valid for 1 hour.
            </DialogDescription>
          </DialogHeader>
          {forgotSent ? (
            <div className="py-4 space-y-3" data-testid="forgot-password-sent-state">
              <p className="text-sm">
                If <strong>{forgotEmail}</strong> matches the master admin's recovery email, a reset link is on its way.
                Check your inbox (and spam folder) — the link expires in 1 hour.
              </p>
              <p className="text-xs text-muted-foreground">
                Didn't get it? Make sure your account email is set in Admin → Account, then try again.
              </p>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <Label htmlFor="forgot-email" className="text-xs">Recovery email</Label>
              <Input
                id="forgot-email"
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="you@example.com"
                className="rounded-none"
                disabled={forgotSending}
                data-testid="forgot-password-email-input"
                onKeyDown={(e) => { if (e.key === "Enter" && !forgotSending) handleForgotPassword(); }}
              />
            </div>
          )}
          <DialogFooter>
            {forgotSent ? (
              <Button onClick={() => setForgotOpen(false)} className="rounded-none" data-testid="forgot-password-close-btn">
                Done
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setForgotOpen(false)} className="rounded-none" disabled={forgotSending}>
                  Cancel
                </Button>
                <Button
                  onClick={handleForgotPassword}
                  disabled={forgotSending || !forgotEmail.trim()}
                  className="rounded-none"
                  data-testid="forgot-password-submit-btn"
                >
                  {forgotSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Send reset link
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
